import config from '@/config';
import { TaskStatus } from '@/modules/tasks/taskboard/kanban/constants';
import api from '@/shared/api';
import { useTaskTimerContext } from '@/shared/providers/TaskTimerProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import handleHttpError from '@/shared/services/HandleHttpError';
import cloneDeep from 'lodash-es/cloneDeep';
import { updateTask } from '@/shared/utils/infiniteDataHelper';
import { TaskQueryKey } from './queryKey';
const extractIndexes = (previousTasks, taskToUpdate) => {
    let taskIndex = previousTasks.findIndex(t => t.id === taskToUpdate.id);
    let subTaskIndex = -1;
    if (taskIndex < 0) {
        // eslint-disable-next-line no-restricted-syntax
        for (let i = 0; i < previousTasks.length; i++) {
            subTaskIndex = previousTasks[i].children.findIndex(t => t.id === taskToUpdate.id);
            if (subTaskIndex >= 0) {
                taskIndex = i;
                break;
            }
        }
    }
    return { taskIndex, subTaskIndex };
};
const mapPropertiesAfterStatusChange = (source, target) => {
    if (!source || !target)
        return;
    target.status = source.status;
    target.isBlocked = source.isBlocked;
    target.inapplicable = source.inapplicable;
    target.rejected = source.rejected;
    target.taskColor = source.taskColor;
};
export const useUpdateTaskStatus = () => {
    const intl = useIntl();
    const queryClient = useQueryClient();
    const { startStopTimerAfterStatusChange } = useTaskTimerContext();
    const { mutateAsync, isLoading, error } = useMutation(values => api.base
        .put(`${config.appUrls.tasks}/api/plannedTasks/UpdateStatus`, values)
        .then(res => res.data), {
        onMutate: async (taskToUpdate) => {
            var _a, _b;
            // Cancel any outgoing refetches
            // (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({ queryKey: [TaskQueryKey.PlannedTasks] });
            // Snapshot the previous value
            const previousTasks = queryClient.getQueryData([TaskQueryKey.PlannedTasks]);
            // spread operator does not work for creating deep copies. Only for simple arrays like [1,2,3]. When we change the task status below, the original task is also overridden if we use the spread operator
            const updatedTasks = cloneDeep(previousTasks);
            let mainTask = null;
            let subTaskToUpdate = null;
            // find the task which will be updated and set the state as we expect it to be after update.
            const { taskIndex, subTaskIndex } = extractIndexes((_a = previousTasks === null || previousTasks === void 0 ? void 0 : previousTasks.pages) === null || _a === void 0 ? void 0 : _a.flat(1), taskToUpdate);
            if (taskIndex >= 0) {
                mainTask = (_b = updatedTasks.pages) === null || _b === void 0 ? void 0 : _b.flat(1)[taskIndex];
                if (subTaskIndex >= 0) {
                    subTaskToUpdate = mainTask.children[subTaskIndex];
                    subTaskToUpdate.status = taskToUpdate.status;
                    if (subTaskToUpdate.status === TaskStatus.Done) {
                        subTaskToUpdate.taskColor = 0;
                    }
                    const amountDone = mainTask.children.filter(f => f.status === TaskStatus.Done).length;
                    const amountTotal = mainTask.children.length;
                    if (amountDone === amountTotal) {
                        mainTask.status = TaskStatus.Done;
                        mainTask.taskColor = 0;
                    }
                }
                else {
                    mainTask.status = taskToUpdate.status;
                    mainTask.inapplicable = taskToUpdate.inapplicable;
                    mainTask.rejected = taskToUpdate.rejected;
                    if (mainTask.status === TaskStatus.Done ||
                        taskToUpdate.rejected ||
                        taskToUpdate.inapplicable) {
                        mainTask.taskColor = 0;
                        mainTask.children = mainTask.children.map(m => (Object.assign(Object.assign({}, m), { status: TaskStatus.Done, rejected: taskToUpdate.rejected, inapplicable: taskToUpdate.inapplicable, taskColor: 0 })));
                    }
                }
            }
            // Optimistically update to the new value
            queryClient.setQueryData([TaskQueryKey.PlannedTasks], updatedTasks);
            // Return a context with the previous and updated tasks
            return {
                previousTasks,
                updatedTasks,
                task: subTaskToUpdate !== null && subTaskToUpdate !== void 0 ? subTaskToUpdate : mainTask,
            };
        },
        onSuccess: async (data, taskToUpdate, context) => {
            const updatedMainTask = data.find(x => x.id && !x.parentId);
            const updatedSubtasks = data.filter(x => x.id && x.parentId);
            // Apply the server result to the cache immutably: updateTask returns new
            // references at every level it touches (page, main task, children array,
            // changed child), so the [task.children] effect in KanbanBoard reliably
            // re-runs and reshuffles the columns.
            queryClient.setQueryData([TaskQueryKey.PlannedTasks], (current) => {
                if (!current)
                    return current;
                let next = current;
                if (updatedMainTask) {
                    next = updateTask(next, updatedMainTask.id, old => {
                        mapPropertiesAfterStatusChange(updatedMainTask, old);
                        return old;
                    });
                }
                updatedSubtasks.forEach(updatedSubtask => {
                    next = updateTask(next, updatedSubtask.id, old => {
                        mapPropertiesAfterStatusChange(updatedSubtask, old);
                        return old;
                    });
                });
                return next;
            });
            queryClient.invalidateQueries([TaskQueryKey.KPI]);
            if (taskToUpdate.inapplicable || taskToUpdate.rejected) {
                queryClient.invalidateQueries([TaskQueryKey.PlannedTasks]);
            }
            const { task } = context;
            if (task) {
                await startStopTimerAfterStatusChange(task);
            }
        },
        // If the mutation fails,
        // use the context returned from onMutate to roll back
        onError: (err, taskToUpdate, context) => {
            queryClient.setQueryData([TaskQueryKey.PlannedTasks], context.previousTasks);
            handleHttpError(intl)(err);
        },
    });
    return {
        updateTaskStatus: mutateAsync,
        isLoading,
        error,
    };
};
